import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, Bot, User } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card } from './ui/card';

// Knowledge base for the chatbot
const KNOWLEDGE_BASE = {
  // Pricing
  "price": "We have two plans: Starter (150 minutes/month) and Pro (400 minutes/month), plus a one-time setup fee. Extra minutes are $0.25/min. Texting is an optional $25/month add-on. See our Pricing page for current prices. Extra locations can be added to the same account.",
  "cost": "We have two plans: Starter (150 minutes/month) and Pro (400 minutes/month), plus a one-time setup fee. Extra minutes are $0.25/min. Texting is an optional $25/month add-on. See our Pricing page for current prices. Extra locations can be added to the same account.",
  "pricing": "We have two plans: Starter (150 minutes/month) and Pro (400 minutes/month), plus a one-time setup fee. Extra minutes are $0.25/min. Texting is an optional $25/month add-on. See our Pricing page for current prices. Extra locations can be added to the same account.",
  "how much": "We have two plans: Starter (150 minutes/month) and Pro (400 minutes/month), plus a one-time setup fee. Extra minutes are $0.25/min. Texting is an optional $25/month add-on. See our Pricing page for current prices. Extra locations can be added to the same account.",
  
  // Minutes
  "minutes": "Starter includes 150 minutes and Pro includes 400 minutes per month, shared across your locations. Extra minutes are $0.25 per minute.",
  "overage": "Once you use your included minutes (150 on Starter, 400 on Pro), extra minutes are $0.25 per minute.",
  "additional minutes": "Once you use your included minutes (150 on Starter, 400 on Pro), extra minutes are $0.25 per minute.",
  
  // Phone Numbers
  "phone number": "When you sign up, you'll choose a dedicated local phone number for your Pro. You pick the state and area code, and we assign a number instantly. When callers need a real person, Front Desk Pro transfers them to your designated number.",
  "new number": "During signup, you select your state and preferred area code, then choose from available local numbers. Your Pro phone number is set up instantly and ready to receive calls.",
  "transfer": "When a caller asks to speak to a real person, Front Desk Pro immediately transfers them to your designated number. No lengthy menus or screening—just a direct transfer.",
  
  // Text/SMS
  "text": "Texting is an optional add-on for $25/month. Appointment confirmations, reminders and owner alerts are sent from the same number your Pro answers.",
  "sms": "Texting is an optional add-on for $25/month. Appointment confirmations, reminders and owner alerts are sent from the same number your Pro answers.",
  "unlimited text": "Texting is an optional add-on for $25/month. Appointment confirmations, reminders and owner alerts are sent from the same number your Pro answers.",
  
  // After Hours
  "after hours": "You control what happens after hours. Front Desk Pro can: transfer to your number, take a message, or send to voicemail. You configure this during setup.",
  "emergency": "You can set up priority routing for urgent keywords. If a caller says 'emergency' or 'urgent', Front Desk Pro can immediately transfer to your designated emergency contact.",
  "voicemail": "Front Desk Pro can send calls to your company voicemail after hours, or take a message and text/email it to you. You choose during setup.",
  
  // Calendar
  "calendar": "Front Desk Pro integrates with Google Calendar and Outlook. It checks your real-time availability and books appointments directly to your calendar.",
  "appointment": "Front Desk Pro can book appointments directly into your Google or Outlook calendar, check your availability in real-time, and send calendar invites to customers.",
  "booking": "Front Desk Pro handles appointment booking automatically. It checks your calendar, finds available slots, books the appointment, and sends confirmation texts.",
  
  // Setup
  "setup": "After you sign up, our team schedules a 15-minute call to learn about your business. Within 24-48 hours, your custom Front Desk Pro is live and answering calls.",
  "setup fee": "There is a small one-time setup fee that covers configuring your Pro with your business info, services and personality. See the Pricing page for the current amount.",
  "how long": "Front Desk Pro is typically live within 24-48 hours after your setup call. We configure everything for your specific business.",
  
  // How it works
  "how does it work": "Front Desk Pro answers your calls 24/7, handles customer questions, books appointments, and transfers calls when needed. You keep your business number or get a new one. Callers who need a real person get transferred directly to you.",
  "what can it do": "Front Desk Pro can: answer questions about your business, book appointments, send text confirmations/reminders, take messages, transfer to a real person when needed, and handle calls 24/7 including nights and holidays.",
  
  // Support
  "support": "Front Desk Pro includes email support. We're here to help you succeed! Contact us at support@frontdeskpro.org.",
  "help": "Need help? Email us at support@frontdeskpro.org or visit our FAQ page.",
  "contact": "You can reach us at support@frontdeskpro.org.",
  
  // Trial/Demo
  "trial": "Try our free demo—no credit card required! Enter your website URL and test how Front Desk Pro sounds for your business. When you're ready, sign up and we'll configure it specifically for you.",
  "demo": "Click 'Try Free Demo' to test Front Desk Pro with your business info. No signup required. Just enter your website URL and have a conversation with your Pro.",
  "free": "Our demo is completely free, no credit card required. Try it to hear how Front Desk Pro sounds for your business, then pick Starter or Pro on our Pricing page.",
  
  // Cancel
  "cancel": "You can cancel anytime. There are no long-term contracts. The one-time setup fee is non-refundable, but monthly subscriptions can be cancelled at any time.",
  "refund": "Monthly subscription fees can be cancelled anytime. The one-time setup fee is non-refundable since it covers the work our team does to configure your Pro.",
  
  // Default
  "default": "I'm here to help! You can ask me about pricing, how Front Desk Pro works, phone number setup, calendar integration, text messages, or anything else. What would you like to know?"
};

const pricingAnswer = (plans) => {
  if (!plans?.length) return null;
  const list = plans.map((p) => `${p.name}: $${p.monthly_price}/month with ${p.minutes_included} minutes${p.setup_fee ? ` (+$${p.setup_fee} one-time setup)` : ''}`).join('. ');
  return `${list}. Extra minutes are $0.25/min. Texting is an optional $25/month add-on. Cancel anytime.`;
};

const PRICE_WORDS = ['price', 'cost', 'pricing', 'how much', 'plan', 'starter', 'pro plan', 'setup fee'];

const findAnswer = (question, plans) => {
  const q = question.toLowerCase();
  const live = pricingAnswer(plans);
  if (live && PRICE_WORDS.some((w) => q.includes(w))) return live;
  
  // Check each keyword
  for (const [keyword, answer] of Object.entries(KNOWLEDGE_BASE)) {
    if (keyword !== 'default' && q.includes(keyword)) {
      return answer;
    }
  }
  
  // Check for greetings
  if (q.match(/^(hi|hello|hey|good morning|good afternoon|good evening)/)) {
    return "Hello! I'm here to answer your questions about Front Desk Pro. What would you like to know? You can ask about pricing, how it works, setup, or anything else!";
  }
  
  // Check for thanks
  if (q.match(/(thank|thanks|thx)/)) {
    return "You're welcome! Is there anything else I can help you with?";
  }
  
  return KNOWLEDGE_BASE.default;
};

const AIChatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: 'assistant', content: "Hi! I'm here to answer your questions about Front Desk Pro. What would you like to know?" }
  ]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [currentSuggestion, setCurrentSuggestion] = useState(0);
  const messagesEndRef = useRef(null);
  const [plans, setPlans] = useState([]);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/subscription/plans`)
      .then((r) => r.json()).then((d) => setPlans(d.plans || [])).catch(() => {});
  }, []);

  // Rotating suggestions
  const suggestions = [
    "Need help building your Pro?",
    "Want us to set up your prompt?",
    "Questions about API integration?",
    "Need help connecting your systems?",
    "Not sure where to start?",
    "Want a personalized demo?"
  ];

  // Rotate suggestions every 3 seconds
  useEffect(() => {
    if (isOpen) return; // Don't rotate when chat is open
    const interval = setInterval(() => {
      setCurrentSuggestion(prev => (prev + 1) % suggestions.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [isOpen, suggestions.length]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const sessionId = useRef(
    sessionStorage.getItem('fdp_support_session') ||
    (() => { const id = `s-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`; sessionStorage.setItem('fdp_support_session', id); return id; })()
  );

  const handleSend = async () => {
    if (!input.trim() || isTyping) return;

    const userMessage = input.trim();
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setInput('');
    setIsTyping(true);

    let answer;
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/support-chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId.current, message: userMessage }),
      });
      if (!res.ok) throw new Error('chat failed');
      answer = (await res.json()).reply;
    } catch {
      answer = findAnswer(userMessage, plans);
    }
    setMessages(prev => [...prev, { role: 'assistant', content: answer }]);
    setIsTyping(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <>
      {/* Floating Suggestion Bubble */}
      {!isOpen && (
        <div 
          onClick={() => setIsOpen(true)}
          className="fixed bottom-24 right-6 z-50 cursor-pointer animate-fade-in"
          key={currentSuggestion}
        >
          <div className="bg-white rounded-2xl rounded-br-sm shadow-lg border border-slate-200 px-4 py-3 max-w-[200px] animate-bounce-subtle">
            <p className="text-sm text-slate-700 font-medium">{suggestions[currentSuggestion]}</p>
            <div className="absolute -bottom-1 right-4 w-3 h-3 bg-white border-r border-b border-slate-200 transform rotate-45" />
          </div>
        </div>
      )}

      {/* Chat Button */}
      <button
        onClick={() => setIsOpen(true)}
        className={`fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-blue-600 text-white shadow-lg shadow-blue-600/25 flex items-center justify-center hover:scale-110 transition-transform ${isOpen ? 'hidden' : ''}`}
        data-testid="chat-button"
      >
        <MessageCircle className="w-6 h-6" />
      </button>

      {/* Chat Window */}
      {isOpen && (
        <Card className="fixed bottom-6 right-6 z-50 w-[380px] h-[500px] flex flex-col shadow-2xl border-border bg-background rounded-2xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-violet-500 to-cyan-500 p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                <Bot className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-white">Front Desk Pro Help</h3>
                <p className="text-xs text-white/70">Ask me anything</p>
              </div>
            </div>
            <button 
              onClick={() => setIsOpen(false)}
              className="text-white/70 hover:text-white"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.map((msg, i) => (
              <div key={i} className={`flex gap-2 ${msg.role === 'user' ? 'justify-end' : ''}`}>
                {msg.role === 'assistant' && (
                  <div className="w-8 h-8 rounded-full bg-violet-500/20 flex items-center justify-center shrink-0">
                    <Bot className="w-4 h-4 text-violet-500" />
                  </div>
                )}
                <div className={`max-w-[80%] rounded-2xl px-4 py-2 text-sm ${
                  msg.role === 'user' 
                    ? 'bg-violet-500 text-white rounded-tr-none' 
                    : 'bg-muted text-foreground rounded-tl-none'
                }`}>
                  {msg.content}
                </div>
                {msg.role === 'user' && (
                  <div className="w-8 h-8 rounded-full bg-gray-500/20 flex items-center justify-center shrink-0">
                    <User className="w-4 h-4 text-gray-500" />
                  </div>
                )}
              </div>
            ))}
            {isTyping && (
              <div className="flex gap-2">
                <div className="w-8 h-8 rounded-full bg-violet-500/20 flex items-center justify-center shrink-0">
                  <Bot className="w-4 h-4 text-violet-500" />
                </div>
                <div className="bg-muted rounded-2xl rounded-tl-none px-4 py-2">
                  <div className="flex gap-1">
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="p-4 border-t border-border">
            <div className="flex gap-2">
              <Input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Ask a question..."
                className="flex-1"
                data-testid="chat-input"
              />
              <Button 
                onClick={handleSend}
                size="icon"
                className="bg-violet-500 hover:bg-violet-600"
                data-testid="chat-send"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
            <p className="text-xs text-muted-foreground text-center mt-2">
              Powered by Front Desk Pro
            </p>
          </div>
        </Card>
      )}
    </>
  );
};

export default AIChatbot;
